<template>
  <div class="card-page">
    <div v-if="loading" class="card-state">名片加载中...</div>
    <div v-else-if="error" class="card-state">{{ error }}</div>
    <template v-else>
      <section class="business-card" id="print-card">
        <div class="card-topline"></div>
        <div class="identity-row">
          <div class="avatar-box">
            <img v-if="avatarSrc" :src="avatarSrc" alt="头像/Logo" />
            <span v-else>{{ avatarText }}</span>
          </div>
          <div class="identity-main">
            <div class="brand-mark">CoRecogN · 穗织</div>
            <h1>{{ displayName }}</h1>
            <div class="tag-row">
              <span class="type-tag">{{ card.supplierTypeText }}</span>
              <span v-for="badge in badges" :key="badge.type + badge.name" class="owner-badge">{{ badge.text }} {{ badge.name }}</span>
            </div>
          </div>
        </div>

        <div class="id-panel">
          <div>
            <span>平台 ID</span>
            <strong>{{ card.shortCode || '-' }}</strong>
          </div>
          <div>
            <span>登录编号</span>
            <strong>{{ card.vendorCode || '-' }}</strong>
          </div>
        </div>

        <div class="content-grid">
          <div class="info-section">
            <h2>基本信息</h2>
            <div class="info-list">
              <div v-for="item in profileItems" :key="item.label" class="info-line">
                <span>{{ item.label }}</span>
                <strong>{{ item.value || '-' }}</strong>
              </div>
            </div>
          </div>

          <div class="info-section sn-section">
            <h2>已创建 SN</h2>
            <div class="sn-total">{{ sn.total || 0 }}</div>
            <div v-if="sn.items && sn.items.length" class="sn-list">
              <div v-for="item in sn.items" :key="item.sn" class="sn-item">
                <span>{{ item.sn }}</span>
                <em>{{ item.status }}</em>
              </div>
            </div>
            <div v-else class="empty-text">暂无 SN</div>
          </div>
        </div>

        <div v-if="card.authorHomeUrl" class="home-section" :style="homeSectionStyle">
          <div>
            <h2>第三方主页</h2>
            <p>{{ card.authorHomeUrl }}</p>
          </div>
          <canvas ref="homeQr" class="qr-canvas"></canvas>
        </div>
      </section>

      <div class="card-actions no-print">
        <el-button v-if="card.authorHomeUrl" type="primary" @click="openHome">访问主页</el-button>
        <el-button @click="printCard">打印名片</el-button>
      </div>
    </template>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import { avatarAssetUrl } from '../../api/avatar'
import { getVendorCard } from '../../api/card'

export default {
  data() {
    return {
      loading: true,
      error: '',
      card: {
        supplierType: 'individual',
        supplierTypeText: '个人',
        profile: {},
        sn: { total: 0, items: [] },
      },
    }
  },
  computed: {
    profile() {
      return this.card.profile || {}
    },
    sn() {
      return this.card.sn || { total: 0, items: [] }
    },
    badges() {
      return this.card.badges || []
    },
    homeSectionStyle() {
      const color = this.card.authorHomeThemeColor
      return color ? { background: `linear-gradient(135deg, ${color}, ${this.lightenColor(color)})` } : {}
    },
    displayName() {
      if (this.card.supplierType === 'circle') return this.profile.circleName || this.card.name
      if (this.card.supplierType === 'certified_company') return this.profile.companyName || this.card.name
      return this.profile.contactName || this.card.name
    },
    avatarText() {
      return (this.displayName || 'C').slice(0, 1).toUpperCase()
    },
    avatarSrc() {
      return avatarAssetUrl(this.card.avatarUrl)
    },
    profileItems() {
      if (this.card.supplierType === 'circle') {
        return [
          { label: '社团名称', value: this.profile.circleName },
          { label: '主理人', value: this.profile.ownerName },
          { label: '联系方式', value: this.profile.contactMethod },
          { label: '社团主页', value: this.profile.homepageUrl },
          { label: '简介', value: this.profile.description },
        ]
      }
      if (this.card.supplierType === 'certified_company') {
        return [
          { label: '企业名称', value: this.profile.companyName },
          { label: '法定代表人', value: this.profile.legalRepresentative },
          { label: '经营范围', value: this.profile.businessScope },
          { label: '认证状态', value: this.qualificationStatusText(this.profile.status) },
        ]
      }
      return [
        { label: '联系人', value: this.profile.contactName },
        { label: '联系方式', value: this.profile.contactMethod },
        { label: '联系邮箱', value: this.profile.contactEmail },
        { label: '联系地址', value: this.profile.contactAddress },
      ]
    },
  },
  created() {
    document.title = 'CoRecogN 名片'
    this.loadCard()
  },
  methods: {
    qualificationStatusText(status) {
      return { pending: '待认证', approved: '已认证', rejected: '未通过' }[status] || '-'
    },
    lightenColor(color) {
      const value = color.replace('#', '')
      if (!/^[0-9a-fA-F]{6}$/.test(value)) return '#f8fafc'
      const red = parseInt(value.slice(0, 2), 16)
      const green = parseInt(value.slice(2, 4), 16)
      const blue = parseInt(value.slice(4, 6), 16)
      const mix = channel => Math.round(channel + (255 - channel) * 0.72)
      return `rgb(${mix(red)}, ${mix(green)}, ${mix(blue)})`
    },
    async loadCard() {
      try {
        const { data } = await getVendorCard(this.$route.params.vendorCode)
        this.card = data
        this.loading = false
        this.$nextTick(this.renderHomeQr)
      } catch (error) {
        this.error = error.response?.data?.message || '名片加载失败'
        this.loading = false
      }
    },
    renderHomeQr() {
      if (!this.card.authorHomeUrl || !this.$refs.homeQr) return
      QRCode.toCanvas(this.$refs.homeQr, this.card.authorHomeUrl, { width: 132, margin: 1 })
    },
    openHome() {
      window.open(this.card.authorHomeUrl, '_blank', 'noopener,noreferrer')
    },
    printCard() {
      window.print()
    },
  },
}
</script>

<style scoped>
.card-page {
  min-height: 100vh;
  padding: 40px 16px;
  background: radial-gradient(circle at top left, #f0f7ff, #f8fafc 45%, #eef2f7);
  color: #1f2937;
}

.card-state {
  max-width: 720px;
  margin: 80px auto;
  padding: 32px;
  border-radius: 20px;
  background: #fff;
  text-align: center;
  color: #64748b;
}

.business-card {
  max-width: 760px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.14);
}

.card-topline {
  height: 8px;
  background: linear-gradient(90deg, #2563eb, #7c3aed, #f97316);
}

.identity-row {
  display: flex;
  gap: 22px;
  align-items: center;
  padding: 32px 36px 22px;
}

.avatar-box {
  width: 96px;
  height: 96px;
  flex: 0 0 96px;
  overflow: hidden;
  border-radius: 28px;
  background: linear-gradient(135deg, #1d4ed8, #7c3aed);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 42px;
  font-weight: 700;
}

.avatar-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-mark {
  margin-bottom: 8px;
  color: #64748b;
  font-size: 13px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.identity-main h1 {
  margin: 0;
  font-size: 32px;
  line-height: 1.2;
}

.tag-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.type-tag,
.owner-badge {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}

.type-tag {
  background: #dbeafe;
  color: #1d4ed8;
}

.owner-badge {
  background: #fff7ed;
  color: #c2410c;
}

.id-panel {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 0 36px 24px;
}

.id-panel div {
  padding: 16px;
  border-radius: 18px;
  background: #f8fafc;
}

.id-panel span,
.info-line span {
  display: block;
  margin-bottom: 6px;
  color: #64748b;
  font-size: 12px;
}

.id-panel strong {
  font-size: 22px;
  letter-spacing: 0.08em;
}

.content-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 16px;
  padding: 0 36px 28px;
}

.info-section {
  padding: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
}

.info-section h2,
.home-section h2 {
  margin: 0 0 14px;
  font-size: 18px;
}

.info-line + .info-line {
  margin-top: 12px;
}

.info-line strong {
  font-weight: 600;
  word-break: break-all;
}

.sn-total {
  margin-bottom: 12px;
  color: #2563eb;
  font-size: 42px;
  font-weight: 700;
}

.sn-item {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-top: 1px solid #eef2f7;
  font-size: 13px;
}

.sn-item span {
  word-break: break-all;
}

.sn-item em {
  color: #64748b;
  font-style: normal;
}

.empty-text {
  color: #94a3b8;
}

.home-section {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;
  margin: 0 36px 36px;
  padding: 20px;
  border-radius: 22px;
  background: #0f172a;
  color: #fff;
}

.home-section p {
  margin: 0;
  color: #cbd5e1;
  word-break: break-all;
}

.qr-canvas {
  width: 132px;
  height: 132px;
  padding: 8px;
  border-radius: 12px;
  background: #fff;
}

.card-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
}

@media (max-width: 640px) {
  .card-page {
    padding: 16px;
  }

  .identity-row,
  .home-section {
    flex-direction: column;
    align-items: flex-start;
  }

  .identity-row,
  .id-panel,
  .content-grid {
    padding-left: 20px;
    padding-right: 20px;
  }

  .id-panel,
  .content-grid {
    grid-template-columns: 1fr;
  }

  .home-section {
    margin-left: 20px;
    margin-right: 20px;
  }

  .identity-main h1 {
    font-size: 26px;
  }
}

@media print {
  @page {
    size: 90mm 54mm;
    margin: 0;
  }

  .card-page {
    min-height: auto;
    padding: 0;
    background: #fff;
  }

  .no-print,
  .card-actions {
    display: none !important;
  }

  .business-card {
    width: 90mm;
    height: 54mm;
    margin: 0;
    border-radius: 0;
    box-shadow: none;
    overflow: hidden;
  }

  .card-topline {
    height: 2mm;
  }

  .identity-row {
    gap: 3mm;
    padding: 4mm 5mm 2mm;
  }

  .avatar-box {
    width: 16mm;
    height: 16mm;
    flex-basis: 16mm;
    border-radius: 4mm;
    font-size: 18pt;
  }

  .brand-mark {
    margin-bottom: 1mm;
    font-size: 6pt;
  }

  .identity-main h1 {
    font-size: 13pt;
  }

  .tag-row {
    margin-top: 1.5mm;
  }

  .type-tag,
  .owner-badge {
    min-height: 5mm;
    padding: 0 2mm;
    font-size: 6pt;
  }

  .id-panel {
    grid-template-columns: repeat(2, 1fr);
    gap: 2mm;
    padding: 0 5mm 2mm;
  }

  .id-panel div {
    padding: 2mm;
    border-radius: 2mm;
  }

  .id-panel span,
  .info-line span {
    margin-bottom: 0.5mm;
    font-size: 5.5pt;
  }

  .id-panel strong {
    font-size: 9pt;
  }

  .content-grid {
    grid-template-columns: 1.35fr 0.8fr;
    gap: 2mm;
    padding: 0 5mm 2mm;
  }

  .info-section {
    padding: 2mm;
    border-radius: 2mm;
  }

  .info-section h2,
  .home-section h2 {
    margin-bottom: 1mm;
    font-size: 7pt;
  }

  .info-line + .info-line {
    margin-top: 1mm;
  }

  .info-line strong,
  .sn-item {
    font-size: 6pt;
  }

  .sn-total {
    margin-bottom: 1mm;
    font-size: 14pt;
  }

  .sn-list .sn-item:nth-child(n + 4) {
    display: none;
  }

  .home-section {
    margin: 0 5mm;
    padding: 2mm;
    border-radius: 2mm;
  }

  .home-section p {
    font-size: 5.5pt;
  }

  .qr-canvas {
    width: 16mm !important;
    height: 16mm !important;
    padding: 1mm;
  }
}
</style>
